import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./primitives";
import BookingWizard from "./BookingWizard";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { REASONS } from "../../lib/siteData";
import { MapPin, Phone, Clock, Mail, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", reason: "", note: "" });
  const [loading, setLoading] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    axios.get(`${API}/contact-info`).then((res) => setContactInfo(res.data)).catch(() => {});
    axios.get(`${API}/clinic-locations`).then((res) => setLocations(res.data)).catch(() => {});
  }, []);

  const INFO = [
    { icon: Phone, k: "Phone", v: contactInfo?.phone || "Phone number pending confirmation" },
    { icon: Mail, k: "Email", v: contactInfo?.email || "Email pending confirmation" },
    { icon: Clock, k: "OPD hours", v: contactInfo?.hours || "Consultation hours pending confirmation" },
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.reason) {
      toast.error("Please add your name, phone and reason for visit.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, form);
      toast.success("Request received. Dr. Chanduka's team will be in touch to confirm.");
      setForm({ name: "", phone: "", reason: "", note: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-[#14263D] pt-12 md:pt-16 pb-24 md:pb-32 relative overflow-hidden" data-testid="contact-section">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(45% 45% at 85% 30%, rgba(111,211,222,0.09), transparent 70%)" }}
      />
      <div className="relative max-w-[1240px] mx-auto px-5 md:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16">
        <Reveal>
          <Eyebrow tone="onDark">09 — Book a consultation</Eyebrow>
          <h2 className="font-display font-light text-4xl md:text-5xl text-white mt-5 leading-tight">
            Request an appointment
          </h2>
          <p className="text-[#F3F5F4]/60 mt-5 leading-relaxed max-w-md">
            Share a few details and the team will reach out to confirm a time. Real clinic
            details are being finalised and will appear here shortly.
          </p>

          {locations.length > 0 && (
            <div className="mt-8 space-y-4">
              {locations.map((loc) => (
                <div key={loc.id} className="flex items-start gap-4">
                  <span className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-[#5ECCA0] flex items-center justify-center shrink-0">
                    <MapPin size={18} />
                  </span>
                  <div>
                    <div className="font-mono-ui text-[0.62rem] uppercase tracking-widest text-[#5ECCA0]">
                      {loc.clinic_name} — {loc.city}
                    </div>
                    <div className="text-[#F3F5F4]/80 text-sm mt-1">{loc.address}</div>
                    {(loc.available_days?.length > 0 || loc.time_range) && (
                      <div className="text-[#F3F5F4]/50 text-xs mt-1">
                        {loc.available_days?.join(", ")} {loc.time_range && `· ${loc.time_range}`}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 space-y-5">
            {INFO.map((r) => (
              <div key={r.k} className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-[#5ECCA0] flex items-center justify-center shrink-0">
                  <r.icon size={18} />
                </span>
                <div>
                  <div className="font-mono-ui text-[0.62rem] uppercase tracking-widest text-[#5ECCA0]">{r.k}</div>
                  <div className="text-[#F3F5F4]/80 text-sm mt-1">{r.v}</div>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <BookingWizard />
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
